import React from "react";
import { useTranslation } from "react-i18next";
import { Button, PopoverTrigger, PopoverContent, Popover, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { ChatModelSelector } from "./ChatModelSelector";
import { TemperatureSelector } from "./TemperatureSelector";
import { ArtefactsPanelToggle } from "./ArtefactsPanelToggle";

interface InvestAIHeaderControlsProps {
  /** The current workflow */
  workflow: { id: string; name: string };
  /** Whether this is a specialized workflow (disables temperature controls) */
  isSpecialized: boolean;
  /** Whether there is content (artefacts/citations) to show toggle for */
  hasContent: boolean;
  /** Whether the artefacts panel is currently visible */
  artefactsVisible: boolean;
  /** Callback to toggle the artefacts panel */
  onToggleArtefacts: () => void;
}

/**
 * Header controls specific to the Invest AI brand.
 * Includes model selector, temperature settings, and content panel toggle.
 */
export const InvestAIHeaderControls: React.FC<InvestAIHeaderControlsProps> = ({
  isSpecialized,
  hasContent,
  artefactsVisible,
  onToggleArtefacts,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-between px-4 items-center">
      <div className="flex items-center">
        <ChatModelSelector position="bottom-start" />
        {!isSpecialized ? (
          <Popover backdrop="transparent" placement="bottom-start">
            <PopoverTrigger>
              <Button
                isIconOnly
                aria-label="Open temperature popover"
                className="bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent outline-none"
              >
                <Icon className="text-brand-primary" icon="solar:settings-bold" width={18} />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="bg-white p-4 rounded-xl shadow-xl w-[280px]">
              <TemperatureSelector />
            </PopoverContent>
          </Popover>
        ) : (
          <Tooltip
            showArrow
            className="max-w-[280px]"
            content={t("disabled-temperature-workflow")}
            placement="bottom-start"
            size="sm"
          >
            <Button
              disabled
              isIconOnly
              aria-label="Open temperature popover"
              className="bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent outline-none disabled:bg-transparent disabled:cursor-not-allowed"
            >
              <Icon className="text-brand-primary" icon="solar:settings-bold" width={18} />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Content panel toggle button */}
      {hasContent && (
        <ArtefactsPanelToggle isVisible={artefactsVisible} onToggle={onToggleArtefacts} />
      )}
    </div>
  );
};
